import { useEffect } from 'react';
import { companyProfile } from '../content/siteContent';

function setMeta(attribute, key, content) {
  if (!content) {
    return;
  }

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', url);
}

function setStructuredData(id, data) {
  let script = document.head.querySelector(`script[data-seo-schema="${id}"]`);

  if (!data) {
    if (script) {
      script.remove();
    }
    return;
  }

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-schema', id);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function absoluteUrl(path) {
  if (!path) {
    return window.location.href;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return new URL(path, window.location.origin).toString();
}

function Seo({ title, description, keywords = [], image = '/logo.jpg', schema }) {
  useEffect(() => {
    const url = window.location.href;
    const keywordText = Array.isArray(keywords) ? keywords.join(', ') : keywords;
    const imageUrl = absoluteUrl(image);

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      name: companyProfile.brandName,
      parentOrganization: companyProfile.legalName,
      url: companyProfile.websiteUrl,
      logo: absoluteUrl('/logo.jpg'),
      email: companyProfile.primaryEmail,
      telephone: companyProfile.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'E-106 Akshardham Heights, S.P. Ring Road, Vastral Road',
        addressLocality: 'Ahmedabad',
        addressRegion: 'Gujarat',
        postalCode: '382418',
        addressCountry: 'IN',
      },
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      name: companyProfile.brandName,
      description: companyProfile.tagline,
      url: companyProfile.websiteUrl,
      image: imageUrl,
      email: companyProfile.primaryEmail,
      telephone: companyProfile.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'E-106 Akshardham Heights, S.P. Ring Road, Vastral Road',
        addressLocality: 'Ahmedabad',
        addressRegion: 'Gujarat',
        postalCode: '382418',
        addressCountry: 'IN',
      },
    };

    const webpageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url,
      inLanguage: 'en-IN',
      isPartOf: { '@type': 'WebSite', name: companyProfile.brandName, url: companyProfile.websiteUrl },
    };

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywordText);
    setMeta('name', 'robots', 'index,follow');
    setMeta('name', 'author', companyProfile.legalName);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', companyProfile.brandName);
    setMeta('property', 'og:locale', 'en_IN');
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', imageUrl);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', imageUrl);
    setCanonical(url);
    setStructuredData('organization', organizationSchema);
    setStructuredData('local-business', localBusinessSchema);
    setStructuredData('webpage', webpageSchema);
    setStructuredData('page-specific', schema || null);
  }, [description, image, keywords, schema, title]);

  return null;
}

export default Seo;
